// src/components/NotificacionEmergente.js
// Modal emergente para notificaciones críticas (correo personalizado y convenios)
// Se muestra al iniciar sesión si hay notificaciones no leídas con requiereConfirmacion:true
import React, { useEffect, useState } from 'react';
import {
    collection,
    doc,
    onSnapshot,
    query,
    updateDoc,
    where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import './NotificacionEmergente.css';

const TIPOS_CRITICOS = ['correo_personalizado', 'convenio_activado', 'recordatorio_corte', 'recordatorio_sancion'];

// Función para sanitizar títulos removiendo caracteres corruptos de emojis rotos
const sanitizarTitulo = (str) => {
    if (!str) return '';
    
    // Lista de secuencias corruptas conocidas al inicio
    const corrupciones = [
        'âš ï¸', 'âš ï¸', 'â ³', 'âš™ï¸', 'âš–ï¸', 
        'ðŸ“¢', 'ðŸ)…âš–ï¸', 'ðŸ)…', 'ðŸ§®', 'ðŸ’°', 
        'âœ…', 'ðŸ“Š', 'ðŸ”„', 'ðŸ“‹', 'â ­ï¸', 
        'ðŸ’§', 'ðŸ”¥', 'ðŸŒ ', 'ðŸ’¡', 'â ¸ï¸', 
        'ðŸ¤ ', 'ðŸ’µ', 'ðŸ  ', 'â „ï¸', '[ADVERTENCIA]'
    ];
    
    let limpio = str;
    for (const corr of corrupciones) {
        if (limpio.startsWith(corr)) {
            limpio = limpio.substring(corr.length);
        }
    }
    
    // Remover espacios o caracteres de control/emoji rotos que puedan quedar al inicio
    // Aseguramos no tocar caracteres normales en español (incluyendo acentuados, números, espacios y corchetes)
    limpio = limpio.replace(/^[^a-zA-Z0-9áéíóúñÁÉÍÓÚÑ¿¡\[\]\s\-]+/, '');
    
    return limpio.trim();
};

const NotificacionEmergente = () => {
    const { currentUser } = useAuth();
    const [pendiente, setPendiente] = useState(null);
    const [visible, setVisible] = useState(false);
    const [confirmando, setConfirmando] = useState(false);

    useEffect(() => {
        if (!currentUser?.uid) return;

        const notifRef = collection(db, 'usuarios', currentUser.uid, 'notificaciones');
        const q = query(
            notifRef,
            where('leida', '==', false),
            where('requiereConfirmacion', '==', true)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            // Buscar la primera notificación crítica no leída
            const criticas = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(n => TIPOS_CRITICOS.includes(n.tipo));

            if (criticas.length > 0) {
                // Ordenar por fecha, mostrar la más antigua primero
                criticas.sort((a, b) => {
                    const fa = a.fecha?.toDate?.() || new Date(0);
                    const fb = b.fecha?.toDate?.() || new Date(0);
                    return fa - fb;
                });
                setPendiente(criticas[0]);
                setVisible(true);
            } else {
                setVisible(false);
                setPendiente(null);
            }
        });

        return () => unsubscribe();
    }, [currentUser?.uid]);

    const handleConfirmar = async () => {
        if (!pendiente || !currentUser?.uid) return;
        setConfirmando(true);
        try {
            const notifRef = doc(db, 'usuarios', currentUser.uid, 'notificaciones', pendiente.id);
            await updateDoc(notifRef, { leida: true });
            setVisible(false);
            setPendiente(null);
        } catch (e) {
            console.error('NotificacionEmergente: error al confirmar', e);
        } finally {
            setConfirmando(false);
        }
    };

    if (!visible || !pendiente) return null;

    const isConvenio = pendiente.tipo === 'convenio_activado';
    const isCorreo = pendiente.tipo === 'correo_personalizado';
    const isCorte = pendiente.tipo === 'recordatorio_corte';
    const isSancion = pendiente.tipo === 'recordatorio_sancion';

    // Determinar clase de la tarjeta
    let cardClass = 'correo';
    if (isConvenio) cardClass = 'convenio';
    if (isCorte) cardClass = 'corte';
    if (isSancion) cardClass = 'sancion';

    // Determinar emoji y título
    let emoji = '📧';
    let titulo = 'Mensaje de la Administración';

    if (isConvenio) {
        emoji = '🤝';
        titulo = 'Convenio de Pago Activado';
    } else if (isCorte) {
        emoji = '⏳';
        titulo = sanitizarTitulo(pendiente.titulo) || 'Recordatorio de Pago';
    } else if (isSancion) {
        emoji = '⚠️';
        titulo = sanitizarTitulo(pendiente.titulo) || 'Aviso de Sanción';
    }

    return (
        <div className="notif-emergente-overlay" role="dialog" aria-modal="true" aria-labelledby="notif-emergente-titulo">
            <div className={`notif-emergente-card ${cardClass}`}>
                <div className="notif-emergente-emoji">
                    {emoji}
                </div>
                <h2 id="notif-emergente-titulo" className="notif-emergente-titulo">
                    {titulo}
                </h2>
                <p className="notif-emergente-descripcion">
                    {pendiente.descripcion || pendiente.titulo}
                </p>
                {isConvenio && (
                    <p className="notif-emergente-aviso">
                        Por favor revisa los detalles del convenio en la sección de Administración antes de continuar.
                    </p>
                )}
                {isCorreo && (
                    <p className="notif-emergente-aviso">
                        Revisa tu correo electrónico registrado en la plataforma para leer el mensaje completo.
                    </p>
                )}
                {(isCorte || isSancion) && (
                    <p className="notif-emergente-aviso">
                        No olvides enviar tu comprobante de pago con Administración, comunícate cuanto antes.
                    </p>
                )}
                <button
                    type="button"
                    className="notif-emergente-btn"
                    onClick={handleConfirmar}
                    disabled={confirmando}
                >
                    {confirmando ? 'Confirmando...' : 'Entendido, continuar'}
                </button>
            </div>
        </div>
    );
};

export default NotificacionEmergente;
